'use client'

import Button from '@eversports/klimt-primitives/alpha/Button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuItem,
} from '@eversports/klimt-primitives/alpha/DropdownMenu'
import { useState } from 'react'
import { useSearchParams } from 'next/navigation'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

import { FILTER_DATA, FILTER_OPTIONS_TRANSLATION_MAPPING } from '../Filters.constants'
import { FILTER_OPTIONS, FILTER_TYPE } from '../Filters.types'

import { UpdateFiltersProps } from './AdditionalFilters'

const ALL_FILTERS_VALUE = 'all'

interface Props {
  filterType: FILTER_TYPE
  selectedFilters: Array<string>
  updateFilters: (values: UpdateFiltersProps) => () => void
  resetFilter: (type: FILTER_TYPE) => () => void
  handleApply: () => void
  undoFilterSelection: () => void
}

const FilterDropdown = ({
  filterType,
  selectedFilters,
  updateFilters,
  resetFilter,
  handleApply,
  undoFilterSelection,
}: Props) => {
  const t = useTranslations('discover-sport-city')

  const [isOpen, setIsOpen] = useState(false)

  const { options, deskTopHeaderTranslationKey, icons } = FILTER_DATA[filterType]

  const searchParams = useSearchParams()
  const params = new URLSearchParams(searchParams.toString())
  const activeFilters = params.getAll(filterType)
  const hasActiveFilters = activeFilters.length > 0

  const activeFiltersTranslations = activeFilters.map((option) =>
    t(FILTER_OPTIONS_TRANSLATION_MAPPING[option as FILTER_OPTIONS]),
  )

  const icon = hasActiveFilters ? icons.selected : icons.default

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
    undoFilterSelection()
  }

  const handleClick = () => {
    handleApply()
    toggleDropdown()
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={toggleDropdown}>
      <DropdownMenuTrigger asChild className="hidden items-center gap-1 sm:flex">
        <Button variant={hasActiveFilters ? 'tertiary-selected' : 'outline'} size="sm">
          <Image src={icon} width={12} height={12} alt="" loading="lazy" />
          {hasActiveFilters ? activeFiltersTranslations.join(', ') : t(deskTopHeaderTranslationKey)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent align="start" className="p-0">
          <DropdownMenuRadioGroup
            value={!selectedFilters.length ? ALL_FILTERS_VALUE : ''}
            onValueChange={resetFilter(filterType)}
          >
            <DropdownMenuRadioItem
              variant="circular"
              value={ALL_FILTERS_VALUE}
              onSelect={(event: Event) => event.preventDefault()}
            >
              {t('all-filters')}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          {options.map((option) => {
            const checked = selectedFilters.includes(option)
            return (
              <DropdownMenuCheckboxItem
                key={option}
                checked={checked}
                onCheckedChange={updateFilters({
                  type: filterType,
                  value: option,
                  maxOptionsCount: options.length,
                })}
                onSelect={(event: Event) => event.preventDefault()}
              >
                {t(FILTER_OPTIONS_TRANSLATION_MAPPING[option as FILTER_OPTIONS])}
              </DropdownMenuCheckboxItem>
            )
          })}
          <DropdownMenuItem asChild>
            <Button onClick={handleClick} className="w-full rounded-none">
              {t('apply-changes')}
            </Button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  )
}

export default FilterDropdown
